import React, { useMemo } from 'react';
import { Section, SkeletonItem } from '@atlaskit/side-navigation';
import { useIntl } from '@atlassian/jira-intl';
import {
	matchUrlPathname,
	matchUrlExact,
	MenuItem,
	shouldRenderNesting,
	getSystemNavigationPaths,
} from '@atlassian/jira-navigation-apps-sidebar-common';

import { SECTION_ID } from '../../../common/constants';
import { useFeatures } from '../../../controllers/features';
import messages from './messages';
import { MigrateCloudSiteMenuItem } from './migrate-to-cloud-site';

export type ImportExportSectionProps = {
	stack: string[];
};

/**
 *  Returns Menu navigation respecting the current user permissions
 *  When updating this file, make sure to also update the following files if necessary:
 * - src/packages/navigation-apps/sidebar/common/src/controllers/use-navigation-path/system
 */
export const ImportExportSection = ({ stack }: ImportExportSectionProps) => {
	const { data: features } = useFeatures();
	const { formatMessage } = useIntl();

	const navigationPath = useMemo(() => getSystemNavigationPaths(), []);

	if (features == null) return <SkeletonItem />;

	return (
		<Section title={formatMessage(messages.importExportGroup)}>
			{features.hasBackupManager && (
				<MenuItem
					analytics={{ itemId: 'cloud-export' }}
					href={navigationPath.importSectionBackupManager}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.backupManager)}
				</MenuItem>
			)}
			{features.hasExternalImport && (
				<MenuItem
					analytics={{ itemId: 'external-import' }}
					href={navigationPath.importSectionExternalImport}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.externalImport)}
				</MenuItem>
			)}
			{features.hasCloudImport && (
				<MenuItem
					analytics={{ itemId: 'cloud-import' }}
					href={navigationPath.importSectionCloudImport}
					selectedOn={matchUrlExact()}
				>
					{formatMessage(messages.importJiraCloud)}
				</MenuItem>
			)}
			{features.hasServerImport && (
				<MenuItem
					analytics={{ itemId: 'server-import' }}
					href={navigationPath.importSectionServerImport}
					selectedOn={matchUrlExact()}
				>
					{formatMessage(messages.importJiraServer)}
				</MenuItem>
			)}
			{shouldRenderNesting(features.hasC2CMigration, stack, SECTION_ID.MIGRATE_TO_CLOUD_SITE) && (
				<MigrateCloudSiteMenuItem />
			)}
		</Section>
	);
};
